import { Injectable, signal } from '@angular/core';
import { ContentScoreData, ContentScoringItem, ContentScoringResult } from 'listings/interfaces/content-scoring';
import { ScrollToField } from 'listings/interfaces/listingsUtils.interface';

@Injectable({ providedIn: 'root' })
export class ListingsScrollUtilsService {
    fieldToScrollTo = signal<ScrollToField | null>(null);

    // Map to store the retry sessions
    // This will clear the intervals when the fieldToScrollTo changes or when the timeout expires.
    // It's needed for the case where a 2nd scrollTo is triggered before the first one fully expires.
    private retrySessions = new Map<string, ReturnType<typeof setTimeout>>();

    finalizeScrollTo() {
        const scrollTo = this.fieldToScrollTo();

        if (!scrollTo) return;

        let element: Element | null = null;
        let scrollClass = '';
        let selector = '';

        if (scrollTo.featureDetailId) {
            selector = `[field-id="${scrollTo.featureDetailId}"]`;
            scrollClass = 'scroll-to-field';
        } else if (scrollTo.itemIdentifier) {
            selector = `[table-room-id="${scrollTo.itemIdentifier}"]`;
            scrollClass = 'scroll-to-table-row';
        } else {
            return;
        }

        element = document.querySelector(selector);

        if (element) {
            document.querySelectorAll(`.${scrollClass}`).forEach((element) => {
                element.classList.remove(scrollClass);
                element.classList.remove('missing-information');
                element.classList.remove('information-provided');
            });

            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            window.scrollTo({ top: element.getBoundingClientRect().top, behavior: 'smooth' });

            if (!scrollTo.suppressHighlight) {
                element.classList.add(scrollClass);
                element.classList.add(scrollTo.highlight ? 'missing-information' : 'information-provided');
            }
            this.fieldToScrollTo.set(null);
        }
    }

    finalizeScrollToRetry() {
        const scrollTo = this.fieldToScrollTo();
        if (!scrollTo) return;

        const key = scrollTo.featureDetailId ?? scrollTo.itemIdentifier;
        if (!key) {
            this.fieldToScrollTo.set(null);
            return;
        }

        // Avoids double-processing the same key
        if (this.retrySessions.has(key)) return;

        const interval = setInterval(() => {
            if (
                this.fieldToScrollTo()?.featureDetailId === scrollTo.featureDetailId ||
                this.fieldToScrollTo()?.itemIdentifier === scrollTo.itemIdentifier
            ) {
                this.finalizeScrollTo();
            }

            if (this.fieldToScrollTo() === null) {
                clearInterval(interval);
                this.retrySessions.delete(key);
            }

            // A lower value in the delay results in smoother trigger for the scrolling and highlight colors,
            // but causes more DOM calls when the DOM takes longer to populate with the dynamic elements.
            // A higher values lowers the number of DOM calls, but results in a less smoother experience,
            // and will create the user the impression that the app briefly froze when they expect the scroll.
            // ↙
        }, 200);

        // Timeout OK
        const timeout = setTimeout(() => {
            clearInterval(interval);
            this.retrySessions.delete(key);

            // Only clears the signal if it's still the same one
            // If a 2nd scrollTo is triggered before the timeout finishes, we don't clear it anymore as that would cause
            // to loose the reference to the new one.
            const current = this.fieldToScrollTo();
            if (current?.featureDetailId === scrollTo.featureDetailId && current?.itemIdentifier === scrollTo.itemIdentifier) {
                this.fieldToScrollTo.set(null);
            }

            // The 5s-ish are needed to account for all possible delays comming from the app, while creating the dynamic fields (setTimeouts, queueMicrotasks, etc),
            // as well as the time itself it takes for the browser to populate the DOM with the new components and show them to the user.
            // This should be the first point to troubleshoot if the behavior appears faulty. It can be increased if needed, as it only affects the time
            // we wait to retry searching for the element in the DOM.
            // ↙
        }, 5000);

        this.retrySessions.set(key, timeout);
    }

    public removeScrollToClass(mappedData: ContentScoringResult) {
        // We need to check if there is an element flagged with scroll-to-field or scroll-to-table-row
        // and remove the class if the item is now completed in the most recent data
        this.removeScrollToFieldClass(mappedData);
        this.removeScrollToTableRowClass(mappedData);
    }

    private removeScrollToFieldClass(mappedData: ContentScoringResult) {
        // This case is for fields or containers
        const scrollToFieldElement = document.querySelector('.scroll-to-field');
        if (scrollToFieldElement) {
            const fieldIdFromElement = scrollToFieldElement.getAttribute('field-id');
            if (fieldIdFromElement && mappedData.contentScoreItems) {
                const scoringItem = mappedData.contentScoreItems.find(
                    (item: ContentScoringItem) => item.featureDetailId === fieldIdFromElement,
                );
                if (scoringItem) {
                    scrollToFieldElement.classList.remove('information-provided');
                    const completedBefore = scrollToFieldElement.classList.contains('information-provided');
                    if ('item' in scoringItem.data) {
                        if (scoringItem.data.item.isCompleted) {
                            scrollToFieldElement.classList.remove('scroll-to-field');
                            scrollToFieldElement.classList.remove('missing-information');
                        }
                        if (scoringItem.data.item.isCompleted === false && completedBefore) {
                            scrollToFieldElement.classList.remove('scroll-to-field');
                            scrollToFieldElement.classList.remove('information-provided');
                        }
                    } else if ('items' in scoringItem.data) {
                        const allCompleted = scoringItem.data.items.every((score: ContentScoreData) => score.isCompleted);
                        if (allCompleted) {
                            scrollToFieldElement.classList.remove('scroll-to-field');
                            scrollToFieldElement.classList.remove('missing-information');
                        }
                    }
                }
            }
        }
    }

    private removeScrollToTableRowClass(mappedData: ContentScoringResult) {
        // This case is for table rows (e.g. Outlets, Room types, etc)
        const scrollToTableRowElement = document.querySelector('.scroll-to-table-row');
        if (scrollToTableRowElement) {
            const tableRoomIdFromElement = scrollToTableRowElement.getAttribute('table-room-id');
            if (tableRoomIdFromElement && mappedData.contentScoreItems) {
                // Table elements like these can only be taken from the inner items (plural) list,
                // as only these multiple results are listed in a table, so we skip looking in the single item
                let contentScoringSubItem: ContentScoreData | undefined;
                mappedData.contentScoreItems.forEach((item: ContentScoringItem) => {
                    if ('items' in item.data) {
                        contentScoringSubItem = item.data.items.find(
                            (subItem: ContentScoreData) => subItem.itemIdentifier === tableRoomIdFromElement,
                        );
                    }
                    return null;
                });
                if (contentScoringSubItem?.isCompleted) {
                    scrollToTableRowElement.classList.remove('scroll-to-table-row');
                    scrollToTableRowElement.classList.remove('missing-information');
                }
            }
        }
    }
}
